import React from 'react'
import styled from '@emotion/styled'
import { useMessageStore } from 'hooks/useGlobalContext/useMessageStore'
import MessageBar from './MessageBar'

const STACK_CAP = 5
const PEEK_OFFSET = 10

const MessageStack = () => {
  const { messages, removeMessage } = useMessageStore()

  const typePriority = { error: 0, message: 1, cta: 2 } as const
  const sorted = [...messages]
    .reverse()
    .sort((a, b) => typePriority[a.type] - typePriority[b.type])
    .slice(0, STACK_CAP)

  const n = sorted.length

  if (n === 0) return null

  return (
    <Stack peekCount={n - 1}>
      {sorted.map((msg, i) => (
        <CardSlot key={msg.id} index={i} total={n}>
          <MessageBar
            msg={msg}
            onDismiss={removeMessage}
            active={i === 0}
            animateHeight={i === 0 && n === 1}
          />
        </CardSlot>
      ))}
    </Stack>
  )
}

const Stack = styled.div<{ peekCount: number }>(
  ({ peekCount }) => `
    position: relative;
    margin: 0 24px;
    padding-top: ${24 + peekCount * PEEK_OFFSET}px;
    padding-bottom: 24px;
    transition: padding-top 300ms ease;
  `
)

const CardSlot = styled.div<{ index: number; total: number }>(
  ({ index, total }) => {
    if (index === 0) {
      return `
        position: relative;
        z-index: ${total};
        opacity: 1;
        transform: scaleX(1);
        transition: opacity 300ms ease, transform 300ms ease;
      `
    }

    const scale = 1 - index * 0.025

    return `
      position: absolute;
      top: ${24 + (total - 1 - index) * PEEK_OFFSET}px;
      left: 0;
      right: 0;
      z-index: ${total - index};
      opacity: ${1 - index * 0.15};
      transform: scaleX(${scale});
      pointer-events: none;
      transition: opacity 300ms ease, transform 300ms ease;
    `
  }
)

export default MessageStack
